import React, { useState, useMemo } from "react";
import type {
  StepMap,
  StepRenderProps,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import {
  FooterButton,
  FooterContainer,
  StepTitle,
  StepContent,
  CheckableOption,
  OptionsSection,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { observer } from "mobx-react-lite";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { posthogClientManager } from "@/manager/posthog-client";
import { MDSText, MDSTextVariant, MDSTextWeight, MDSIcon, MDSIconKind, MDSIconColor } from "@/mds";

interface UseCaseOption {
  id: string;
  title: string;
}

const UseCaseOptions: UseCaseOption[] = [
  { id: "remembering-people", title: "Remembering people and conversations" },
  { id: "researching-info", title: "Researching and connecting information" },
  { id: "turning-ideas", title: "Turning ideas into finished content" },
  { id: "meetings-tasks", title: "Following up on meetings" },
  { id: "juggling-projects", title: "Juggling many clients and projects" },
  { id: "organizing-knowledge", title: "Organizing my team's knowledge" },
  { id: "just-exploring", title: "Just exploring" },
];

const randomizeOptions = (options: UseCaseOption[]): UseCaseOption[] => {
  const lastOption = options.find(option => option.id === "just-exploring");
  const rest = options.filter(option => option.id !== "just-exploring");

  const shuffled = [...rest];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return lastOption ? [...shuffled, lastOption] : shuffled;
};

export const UseCaseStep: React.FC<StepRenderProps> = observer(({ onComplete }) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const randomizedOptions = useMemo(() => randomizeOptions(UseCaseOptions), []);

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleNext = async () => {
    if (!selectedOption) return;

    const selectedOptionTitle = UseCaseOptions.find(opt => opt.id === selectedOption)?.title;
    const properties = {
      selectedOption,
      selectedOptionTitle,
    };
    trackEvent(TrackedEvent.UseCaseStepComplete, properties);

    const posthogUserProperties = {
      use_case_option: selectedOption,
      use_case_label: selectedOptionTitle,
      use_case_completed: true,
      use_case_completed_at: new Date().toISOString(),
    };

    const posthogOnceProperties = {
      initial_use_case_completed_at: new Date().toISOString(),
    };

    posthogClientManager.setPersonProperties(posthogUserProperties, posthogOnceProperties);

    onComplete("use-case");
  };

  return (
    <WizardContentWrapper>
      <StepTitle>What would you like Mem to help with most?</StepTitle>

      <StepContent overflowY="auto">
        <OptionsSection>
          {randomizedOptions.map(option => (
            <CheckableOption
              key={option.id}
              isSelected={selectedOption === option.id}
              onClick={() => handleOptionSelect(option.id)}
            >
              <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                {option.title}
              </MDSText>
              {selectedOption === option.id && (
                <MDSIcon
                  kind={MDSIconKind.CheckCircleSolid}
                  size={16}
                  color={MDSIconColor.Emphasis}
                />
              )}
            </CheckableOption>
          ))}
        </OptionsSection>
      </StepContent>

      <FooterContainer>
        <FooterButton onClick={handleNext} isDisabled={!selectedOption}>
          Next
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

export const UseCaseStepsMap: StepMap = {
  "use-case": {
    id: "use-case",
    render: props => <UseCaseStep {...props} />,
  },
} as const;
